<template>
  <!-- Backdrop -->
  <Teleport to="body">
    <Transition name="cloud-panel-fade">
      <div v-if="open" class="fixed inset-0 z-40 bg-black/30" @click="close()" />
    </Transition>
  </Teleport>

  <!-- Slide-over panel: the cloud account, reachable from the shell bar button
       on every layout (launcher, hub tabs, pages) — the signed-in identity no
       longer changes with the hub the tab is connected to. -->
  <Teleport to="body">
    <Transition name="cloud-panel-slide">
      <aside
        v-if="open"
        class="fixed inset-y-0 right-0 z-50 flex w-80 max-w-[92vw] flex-col overflow-hidden border-l border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-800">
        <!-- Header -->
        <div class="flex items-center gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
          <div
            class="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary-400 text-gray-900">
            <img
              v-if="authStore.cloudUser?.image"
              :src="authStore.cloudUser.image"
              :alt="userName"
              class="h-full w-full object-cover" />
            <span v-else class="text-sm font-bold">{{ userInitials }}</span>
          </div>
          <div class="min-w-0 flex-1">
            <p class="truncate text-sm font-semibold text-gray-900 dark:text-white">{{ userName }}</p>
            <p v-if="userEmail" class="truncate text-xs text-gray-500 dark:text-gray-400">{{ userEmail }}</p>
            <p v-else class="text-xs text-gray-500 dark:text-gray-400">
              {{ tt('Torik cloud account', 'Torik račun v oblaku') }}
            </p>
          </div>
          <button
            type="button"
            class="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200"
            :title="tt('Close', 'Zapri')"
            @click="close()">
            <span class="material-symbols-outlined" style="font-size: 20px">close</span>
          </button>
        </div>

        <!-- Signed out -->
        <div v-if="!isSignedIn" class="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
          <span class="material-symbols-outlined text-gray-300 dark:text-gray-600" style="font-size: 44px"
            >account_circle</span
          >
          <p class="text-sm font-medium text-gray-900 dark:text-white">
            {{ tt('Not signed in', 'Niste prijavljeni') }}
          </p>
          <p class="text-xs text-gray-500 dark:text-gray-400">
            {{
              tt(
                'Sign in with your Torik cloud account to see your organizations, services and messages here.',
                'Prijavite se s svojim računom Torik v oblaku, da tukaj vidite svoje organizacije, storitve in sporočila.',
              )
            }}
          </p>
          <button
            type="button"
            class="mt-2 rounded-full bg-primary-400 px-5 py-2 text-sm font-semibold text-gray-900 transition-colors hover:bg-primary-500"
            :disabled="signingIn"
            @click="signIn">
            {{ signingIn ? tt('Opening…', 'Odpiranje…') : tt('Sign in', 'Prijavite se') }}
          </button>
          <p v-if="signInError" class="text-xs text-red-500">{{ signInError }}</p>
        </div>

        <!-- Signed in -->
        <template v-else>
          <nav class="flex-1 overflow-y-auto p-2">
            <button type="button" :class="[itemClass, itemTextClass]" @click="go('/me')">
              <span :class="iconClass" style="font-size: 18px">manage_accounts</span>
              <span class="min-w-0 flex-1 truncate text-left">{{ tt('My account', 'Moj račun') }}</span>
              <span class="material-symbols-outlined text-gray-400 dark:text-gray-500" style="font-size: 16px"
                >chevron_right</span
              >
            </button>
            <button type="button" :class="[itemClass, itemTextClass]" @click="go('/pair')">
              <span :class="iconClass" style="font-size: 18px">touch_app</span>
              {{ tt('Touch Mode', 'Način dotikalne plošče') }}
            </button>
            <button
              v-if="authStore.isAuthenticated"
              type="button"
              :class="[itemClass, itemTextClass]"
              @click="disconnectHub">
              <span :class="iconClass" style="font-size: 18px">link_off</span>
              {{ tt('Disconnect from hub', 'Prekini povezavo z vozliščem') }}
            </button>
          </nav>

          <div class="border-t border-gray-200 p-2 dark:border-gray-700">
            <button
              type="button"
              :class="[itemClass, 'text-red-500 hover:bg-red-500/10 dark:text-red-400']"
              @click="signOut">
              <span class="material-symbols-outlined text-red-500/80" style="font-size: 18px">logout</span>
              {{ tt('Sign out of cloud account', 'Odjava iz oblačnega računa') }}
            </button>
          </div>
        </template>
      </aside>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
/**
 * Global cloud account panel — opened by the Electron shell's account button
 * (`ui.requestAccountMenu()` triggered from the bar in App.vue) and usable on
 * every layout because the shell bar is global. Shows the cloud account that
 * the app is signed in with; the hub's own user drawer stays on the hub UI's
 * top-right avatar.
 *
 * Deliberately carries no appearance control: the shared picker belonged to
 * `server/AccountMenu.vue`, which nothing ever mounted, and both files were
 * removed (kanban t_7885f1c2). Appearance lives in the hub drawer's own panels
 * and the touch-panel sheet; restoring a control here is a deliberate decision,
 * not an oversight.
 */
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { tt } from '../../utils/consoleText';
import { useAuthStore } from '../../stores/auth';
import { useUIStore } from '../../stores/ui';
import { useWebRTCStore } from '../../stores/webrtc';
import { startCloudOAuthLogin } from '../../services/cloud-oauth';

const router = useRouter();
const authStore = useAuthStore();
const ui = useUIStore();
const webrtcStore = useWebRTCStore();

const open = ref(false);
const signingIn = ref(false);
const signInError = ref('');

const isSignedIn = computed(() => authStore.isCloudAuthenticated);
const userName = computed(
  () => authStore.cloudUser?.name || authStore.cloudUser?.email || tt('Not signed in', 'Niste prijavljeni'),
);
const userEmail = computed(() => (authStore.cloudUser?.name ? authStore.cloudUser?.email || '' : ''));

const userInitials = computed(() => {
  const user = authStore.cloudUser;
  if (!user) return '—';
  if (user.name) {
    const parts = user.name.split(' ');
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : user.name.slice(0, 2).toUpperCase();
  }
  return (user.email || '').slice(0, 2).toUpperCase();
});

const itemClass =
  'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors hover:bg-gray-100 dark:hover:bg-gray-700';
const itemTextClass = 'text-gray-700 hover:text-gray-900 dark:text-gray-200 dark:hover:text-white';
const iconClass = 'material-symbols-outlined text-gray-500 dark:text-gray-400';

watch(
  () => ui.accountMenuSignal,
  () => {
    open.value = true;
    // The account may have been edited on the web — refresh on open (cheap).
    if (authStore.isCloudAuthenticated) void authStore.fetchCloudUser();
  },
);

function close(): void {
  open.value = false;
}

function go(path: string): void {
  close();
  void router.push(path);
}

async function signIn(): Promise<void> {
  signingIn.value = true;
  signInError.value = '';
  const result = await startCloudOAuthLogin();
  signingIn.value = false;
  if (!result.success) {
    signInError.value = result.error || tt('Could not open the sign-in page', 'Prijavne strani ni bilo mogoče odpreti');
    return;
  }
  close();
}

function disconnectHub(): void {
  close();
  if (webrtcStore.connected || webrtcStore.connecting) {
    webrtcStore.disconnect();
  }
  if (authStore.isAuthenticated) {
    authStore.logout({ closeTabs: true });
  }
}

function signOut(): void {
  close();
  if (webrtcStore.connected || webrtcStore.connecting) {
    webrtcStore.disconnect();
  }
  authStore.logoutFromCloud({ closeTabs: true });
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape' && open.value) close();
}

onMounted(() => window.addEventListener('keydown', onKeydown));
onUnmounted(() => window.removeEventListener('keydown', onKeydown));
</script>

<style scoped>
.cloud-panel-fade-enter-active,
.cloud-panel-fade-leave-active {
  transition: opacity 0.15s ease;
}
.cloud-panel-fade-enter-from,
.cloud-panel-fade-leave-to {
  opacity: 0;
}

.cloud-panel-slide-enter-active,
.cloud-panel-slide-leave-active {
  transition: transform 0.2s ease;
}
.cloud-panel-slide-enter-from,
.cloud-panel-slide-leave-to {
  transform: translateX(100%);
}
</style>
